import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Space, Spin, Statistic } from 'antd';
import { AGE_GROUPS, type AgeGroup } from '../../../constants/ageGroup';
import { GENDERS, type Gender } from '../../../constants/gender';
import {
  ACTIVITY_TYPES,
  type ActivityType,
} from '../../../constants/activityType';
import { TRAVEL_MODES, type TravelMode } from '../../../constants/travelMode';
import type { AttributeAggregationType } from '../../../infrastructure/dataApi/client';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useAttributeAggregationComparisonsData } from '../../../hooks/data/useAttributeAggregationComparisonsData';
import { useSummaryComparisonsData } from '../../../hooks/data/useSummaryComparisonsData';
import { BarChart, type BarItem } from '../Chart/BarChart';
import { SchemaSelector } from './SchemaSelector';

type Props = {
  type: AttributeAggregationType;
  selectedLabels: string[];
  barWidth?: 'normal' | 'wide';
  noDataMessage: string;
  unit?: string;
};

const AttributeAggregationComparison = ({
  type,
  selectedLabels,
  barWidth,
  noDataMessage,
  unit,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { allDataMetas } = useMetaData();
  const { isLoading: isLoadingAggregation, aggregationsMap } =
    useAttributeAggregationComparisonsData(type);
  const { isLoading: isLoadingSummary, summariesMap } =
    useSummaryComparisonsData();

  const data = Object.fromEntries(
    Object.entries(aggregationsMap).map(
      ([simDataId, aggregations]: [string, Map<string, number>]): [
        string,
        BarItem[],
      ] => {
        const dataMeta = allDataMetas.get(simDataId);
        switch (type) {
          case 'gender':
            return [
              simDataId,
              GENDERS.map((gender: Gender): BarItem => {
                return {
                  key: gender,
                  label:
                    gender === '1' ? t('genders.male') : t('genders.female'),
                  value: aggregations.get(gender) ?? 0,
                };
              }),
            ];
          case 'age':
            return [
              simDataId,
              AGE_GROUPS.filter((ageGroup: AgeGroup): boolean => {
                if (dataMeta?.type === 'gad_standard') {
                  return !['10', '70'].includes(ageGroup);
                }
                return true;
              }).map((ageGroup: AgeGroup): BarItem => {
                return {
                  key: ageGroup,
                  label:
                    dataMeta?.type === 'gad_standard' && ageGroup === '60'
                      ? t(`ageGroups.60s+`)
                      : t(`ageGroups.${ageGroup}s`),
                  value: aggregations.get(ageGroup) ?? 0,
                };
              }),
            ];
          case 'nationality':
            return [
              simDataId,
              Array.from(aggregations)
                .map(([label, value]: [string, number]): BarItem => {
                  return {
                    key: label,
                    label: t(`countries.${label}`, {
                      defaultValue: t('countries.ZZZ'),
                    }),
                    value: value,
                  };
                })
                .sort((item1: BarItem, item2: BarItem): number => {
                  // 集計値の大きい順にソートする
                  return item2.value - item1.value;
                }),
            ];
          case 'end_activity_type':
            return [
              simDataId,
              ACTIVITY_TYPES.map((activityType: ActivityType): BarItem => {
                return {
                  key: activityType,
                  label: t(`activityType.${activityType}`),
                  value: aggregations.get(activityType) ?? 0,
                };
              }).sort((item1: BarItem, item2: BarItem): number => {
                // 集計値の大きい順にソートしながら、その他を末尾にする
                if (item1.key === 'others') {
                  return 1;
                }
                if (item2.key === 'others') {
                  return -1;
                }
                return item2.value - item1.value;
              }),
            ];
          case 'longest_distance_mode':
            return [
              simDataId,
              TRAVEL_MODES.filter((mode: TravelMode): boolean => {
                // 移動手段については集計が存在するもののみを対象とする
                return aggregations.has(mode);
              })
                .map((mode: TravelMode): BarItem => {
                  return {
                    key: mode,
                    label: t(`travelMode.${mode}`),
                    value: aggregations.get(mode) ?? 0,
                  };
                })
                .sort((item1: BarItem, item2: BarItem): number => {
                  // 集計値の大きい順にソートしながら、その他を末尾にする
                  if (item1.key === 'other') {
                    return 1;
                  }
                  if (item2.key === 'other') {
                    return -1;
                  }
                  return item2.value - item1.value;
                }),
            ];
        }
      }
    )
  );

  return (
    <>
      <SchemaSelector />
      <Spin spinning={isLoadingAggregation || isLoadingSummary}>
        <GridContainer>
          {Object.entries(data).map(
            ([simDataId, items]: [string, BarItem[]]): ReactNode => {
              return (
                <Space
                  key={simDataId}
                  direction="vertical"
                  style={{ width: '100%' }}
                >
                  <Statistic
                    title={<ItemTitle>{t('targetAgentCount')}</ItemTitle>}
                    value={summariesMap[simDataId] ?? '-'}
                    suffix={<ItemSuffix>{t('unit.agent')}</ItemSuffix>}
                  />
                  <BarChart
                    barWidth={barWidth}
                    data={items}
                    selectedLabels={selectedLabels}
                    noDataMessage={noDataMessage}
                    unit={unit}
                  />
                  <GraphTitle>
                    {allDataMetas.get(simDataId)?.name ?? ''}
                  </GraphTitle>
                </Space>
              );
            }
          )}
        </GridContainer>
      </Spin>
    </>
  );
};

const GridContainer = styled.div`
  margin: 16px 0px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const ItemTitle = styled.div`
  color: #595959;
`;

const ItemSuffix = styled.div`
  font-size: 60%;
`;

const GraphTitle = styled.div`
  font-size: 12px;
  text-align: center;
`;

export { AttributeAggregationComparison };
